// ---------------------------------------------------------------------------
// DSS-2: Gradient builder — linear/radial/conic, multi-stop, angle control
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Plus, Trash2 } from "lucide-react";
import { normalizeColorInputValue } from "./color-input-value";

// ── Types ──
export type GradientType = "linear" | "radial" | "conic";
export type RadialShape = "ellipse" | "circle";

export interface GradientStop {
  readonly color: string;
  readonly position: number; // 0-100
}

export interface GradientConfig {
  readonly type: GradientType;
  readonly angle: number; // 0-360 for linear/conic
  readonly radialShape: RadialShape;
  readonly radialCenterX: number; // 0-100
  readonly radialCenterY: number; // 0-100
  readonly stops: readonly GradientStop[];
}

// ── Constants ──
const GRADIENT_TYPES: readonly GradientType[] = ["linear", "radial", "conic"];
const RADIAL_SHAPES: readonly RadialShape[] = ["ellipse", "circle"];
const MIN_STOPS = 2;
const MAX_STOPS = 10;
const MIN_ANGLE = 0;
const MAX_ANGLE = 360;
const ANGLE_STEP = 1;

const GRID_9: readonly [number, number, string][] = [
  [0, 0, "TL"], [50, 0, "TC"], [100, 0, "TR"],
  [0, 50, "CL"], [50, 50, "C"], [100, 50, "CR"],
  [0, 100, "BL"], [50, 100, "BC"], [100, 100, "BR"],
];

const DEFAULT_GRADIENT: GradientConfig = {
  type: "linear",
  angle: 135,
  radialShape: "ellipse",
  radialCenterX: 50,
  radialCenterY: 50,
  stops: [
    { color: "#1040CC", position: 0 },
    { color: "#7C3AED", position: 100 },
  ],
};

// ── CSS Generator ──
export function generateGradientCss(config: GradientConfig): string {
  const stops = config.stops
    .map((s) => `${s.color} ${safeNum(s.position, 0, 100)}%`)
    .join(", ");

  switch (config.type) {
    case "linear":
      return `linear-gradient(${safeNum(config.angle, MIN_ANGLE, MAX_ANGLE)}deg, ${stops})`;
    case "radial": {
      const cx = safeNum(config.radialCenterX, 0, 100);
      const cy = safeNum(config.radialCenterY, 0, 100);
      return `radial-gradient(${config.radialShape} at ${cx}% ${cy}%, ${stops})`;
    }
    case "conic":
      return `conic-gradient(from ${safeNum(config.angle, MIN_ANGLE, MAX_ANGLE)}deg, ${stops})`;
    default:
      return `linear-gradient(135deg, ${stops})`;
  }
}

// ── NaN guard ──
function safeNum(val: number, min: number, max: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(n, min), max);
}

// ── Props ──
interface GradientBuilderProps {
  readonly value: GradientConfig;
  readonly onChange: (config: GradientConfig) => void;
}

// ── Component ──
export default function GradientBuilder({ value, onChange }: GradientBuilderProps) {
  const uid = useId();
  const config = value ?? DEFAULT_GRADIENT;

  const handleTypeChange = useCallback(
    (type: GradientType) => onChange({ ...config, type }),
    [config, onChange],
  );

  const handleAngleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const angle = safeNum(Number(e.target.value), MIN_ANGLE, MAX_ANGLE);
      onChange({ ...config, angle });
    },
    [config, onChange],
  );

  const handleShapeChange = useCallback(
    (shape: RadialShape) => onChange({ ...config, radialShape: shape }),
    [config, onChange],
  );

  const handleCenterClick = useCallback(
    (x: number, y: number) => onChange({ ...config, radialCenterX: x, radialCenterY: y }),
    [config, onChange],
  );

  const handleStopColor = useCallback(
    (idx: number, color: string) => {
      const stops = config.stops.map((s, i) => (i === idx ? { ...s, color } : s));
      onChange({ ...config, stops });
    },
    [config, onChange],
  );

  const handleStopPosition = useCallback(
    (idx: number, raw: string) => {
      const position = safeNum(Number(raw), 0, 100);
      const stops = config.stops.map((s, i) => (i === idx ? { ...s, position } : s));
      onChange({ ...config, stops });
    },
    [config, onChange],
  );

  const handleAddStop = useCallback(() => {
    if (config.stops.length >= MAX_STOPS) return;
    const last = config.stops[config.stops.length - 1];
    const prev = config.stops[config.stops.length - 2];
    const newPos = safeNum(Math.round(((last?.position ?? 100) + (prev?.position ?? 0)) / 2), 0, 100);
    onChange({ ...config, stops: [...config.stops, { color: "#888888", position: newPos }] });
  }, [config, onChange]);

  const handleRemoveStop = useCallback(
    (idx: number) => {
      if (config.stops.length <= MIN_STOPS) return;
      onChange({ ...config, stops: config.stops.filter((_, i) => i !== idx) });
    },
    [config, onChange],
  );

  const gradientCss = generateGradientCss(config);

  return (
    <div className="flex flex-col gap-3">
      {/* Type selector */}
      <div className="flex gap-1">
        {GRADIENT_TYPES.map((t) => (
          <button key={t} type="button" onClick={() => handleTypeChange(t)}
            className={["flex-1 rounded px-2 py-1 text-[10px] font-medium capitalize transition-colors", config.type === t
              ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
              : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
            ].join(" ")}>
            {t}
          </button>
        ))}
      </div>

      {/* Angle control -- linear & conic */}
      {(config.type === "linear" || config.type === "conic") && (
        <div className="flex items-center gap-2">
          <label htmlFor={`${uid}-angle`} className="w-12 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            {config.type === "conic" ? "Start" : "Angle"}
          </label>
          <AngleCircle angle={config.angle} />
          <input id={`${uid}-angle`} type="range" min={MIN_ANGLE} max={MAX_ANGLE} step={ANGLE_STEP} value={config.angle} onChange={handleAngleChange}
            className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]" />
          <span className="w-10 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">{config.angle}deg</span>
        </div>
      )}

      {/* Radial controls */}
      {config.type === "radial" && (
        <>
          <div className="flex gap-1">
            {RADIAL_SHAPES.map((s) => (
              <button key={s} type="button" onClick={() => handleShapeChange(s)}
                className={["flex-1 rounded px-2 py-1 text-[10px] font-medium capitalize transition-colors", config.radialShape === s
                  ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                  : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
                ].join(" ")}>
                {s}
              </button>
            ))}
          </div>
          <PositionGrid activeX={config.radialCenterX} activeY={config.radialCenterY} onClick={handleCenterClick} />
        </>
      )}

      {/* Color stops */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Stops ({config.stops.length})
          </span>
          {config.stops.length < MAX_STOPS && (
            <button
              type="button"
              onClick={handleAddStop}
              className="flex items-center gap-0.5 text-[10px] text-[var(--color-primary)] hover:text-[var(--color-primary)]/80"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          )}
        </div>
        {config.stops.map((stop, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <input
              type="color"
              value={normalizeColorInputValue(stop.color)}
              onChange={(e) => handleStopColor(idx, e.target.value)}
              className="h-5 w-6 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0"
            />
            <input
              type="number"
              min={0}
              max={100}
              value={stop.position}
              onChange={(e) => handleStopPosition(idx, e.target.value)}
              className="w-12 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1.5 py-0.5 text-[10px] tabular-nums text-[var(--color-foreground)]"
            />
            <span className="text-[10px] text-[var(--color-muted-foreground)]">%</span>
            {config.stops.length > MIN_STOPS && (
              <button
                type="button"
                onClick={() => handleRemoveStop(idx)}
                className="ml-auto text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Live preview */}
      <div
        className="h-8 w-full rounded-md border border-[var(--color-border)]"
        style={{ background: gradientCss }}
      />
    </div>
  );
}

// ── Angle circle indicator ──
function AngleCircle({ angle }: { readonly angle: number }) {
  const rad = ((angle - 90) * Math.PI) / 180;
  const RADIUS = 8;
  const cx = 10 + Math.cos(rad) * RADIUS;
  const cy = 10 + Math.sin(rad) * RADIUS;

  return (
    <svg width={20} height={20} className="shrink-0">
      <circle cx={10} cy={10} r={RADIUS} fill="none" stroke="var(--color-border)" strokeWidth={1} />
      <line x1={10} y1={10} x2={cx} y2={cy} stroke="var(--color-primary)" strokeWidth={1.5} />
      <circle cx={cx} cy={cy} r={2} fill="var(--color-primary)" />
    </svg>
  );
}

// ── 9-point position grid ──
function PositionGrid({ activeX, activeY, onClick }: {
  readonly activeX: number; readonly activeY: number;
  readonly onClick: (x: number, y: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">Center</span>
      <div className="grid grid-cols-3 gap-1">
        {GRID_9.map(([x, y, label]) => {
          const isActive = x === activeX && y === activeY;
          return (
            <button key={label} type="button" title={label} onClick={() => onClick(x, y)}
              className={["h-4 w-4 rounded-sm border transition-colors", isActive
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]"
                : "border-[var(--color-border)] bg-[var(--color-muted)] hover:border-[var(--color-primary)]/50",
              ].join(" ")} />
          );
        })}
      </div>
    </div>
  );
}

export { DEFAULT_GRADIENT };
